import { useEffect } from 'react';
import { comprimirImagemParaDataUrl } from '@/utils/comprimir-imagem';

export function useImageUpload(
  /** `t` do `useI18n()` do componente — a mensagem de erro precisa reagir à troca de idioma. */
  t: (chave: string, variaveis?: Record<string, string | number>) => string,
  aberto: boolean,
  fotoParte1: string,
  fotoParte2: string,
  fotoParte3: string,
  fotoParte4: string,
  setFotoParte1: (foto: string) => void,
  setFotoParte2: (foto: string) => void,
  setFotoParte3: (foto: string) => void,
  setFotoParte4: (foto: string) => void
) {
  // Função genérica para upload de imagem por índice (1-4)
  // Suporta HEIC/HEIF automaticamente (converte para JPEG) e comprime para caber no teto de tamanho
  const handleUploadImagem = async (
    file: File,
    indice: 1 | 2 | 3 | 4,
    onErro?: (msg: string | null) => void
  ) => {
    try {
      const result = await comprimirImagemParaDataUrl(file);

      if (indice === 1) setFotoParte1(result);
      else if (indice === 2) setFotoParte2(result);
      else if (indice === 3) setFotoParte3(result);
      else if (indice === 4) setFotoParte4(result);
      onErro?.(null); // limpar erro anterior ao ter sucesso
    } catch (error: any) {
      console.error('Erro ao processar imagem:', error);
      onErro?.(error?.message || t('apreciacaoModais.editarParte.imagens.erroProcessar'));
    }
  };

  // Função para processar múltiplas imagens coladas do clipboard (Ctrl+V)
  // NOTA: Quando múltiplas imagens são copiadas do Word, elas podem vir como:
  // 1. Múltiplos itens de imagem no clipboard (processados individualmente)
  // 2. Uma única imagem composta (limitação do Word - será tratada como uma imagem)
  const handlePasteImagem = async (
    e: React.ClipboardEvent<HTMLDivElement> | ClipboardEvent,
    indiceInicial?: 1 | 2 | 3 | 4
  ) => {
    e.preventDefault();
    const clipboardData = 'clipboardData' in e ? e.clipboardData : (e as ClipboardEvent).clipboardData;
    if (!clipboardData) return;
    const items = clipboardData.items;

    // Coletar todas as imagens do clipboard
    const imagens: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Verificar se é uma imagem (pode ser image/png, image/jpeg, image/jpg, etc.)
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          // Converter blob para File com nome único
          const extensao = item.type.includes('jpeg') || item.type.includes('jpg') ? 'jpg' : 'png';
          const file = new File([blob], `imagem-colada-${i}.${extensao}`, { type: blob.type });
          imagens.push(file);
        }
      }
    }

    if (imagens.length === 0) return;

    // Se um índice específico foi fornecido, usar apenas esse slot
    if (indiceInicial !== undefined) {
      handleUploadImagem(imagens[0], indiceInicial);

      // Mostrar feedback visual se for evento React
      if ('currentTarget' in e && e.currentTarget && e.currentTarget instanceof HTMLElement) {
        const target = e.currentTarget as HTMLElement;
        if (target.style) {
          const originalBg = target.style.backgroundColor || '';
          target.style.backgroundColor = '#dbeafe';
          setTimeout(() => {
            if (target && target.style) {
              target.style.backgroundColor = originalBg;
            }
          }, 300);
        }
      }
      return;
    }

    // Caso contrário, distribuir todas as imagens pelos slots vazios
    const slotsDisponiveis: (1 | 2 | 3 | 4)[] = [];
    if (!fotoParte1) slotsDisponiveis.push(1);
    if (!fotoParte2) slotsDisponiveis.push(2);
    if (!fotoParte3) slotsDisponiveis.push(3);
    if (!fotoParte4) slotsDisponiveis.push(4);

    // Distribuir imagens pelos slots disponíveis
    const imagensParaProcessar = Math.min(imagens.length, slotsDisponiveis.length);
    for (let i = 0; i < imagensParaProcessar; i++) {
      handleUploadImagem(imagens[i], slotsDisponiveis[i]);
    }

    // Feedback: se houver mais imagens do que slots disponíveis
    if (imagens.length > slotsDisponiveis.length) {
      console.log(`Foram coladas ${imagens.length} imagens, mas apenas ${slotsDisponiveis.length} slot(s) disponível(is).`);
    }

    // Mostrar feedback visual se for evento React
    if ('currentTarget' in e && e.currentTarget && e.currentTarget instanceof HTMLElement) {
      const target = e.currentTarget as HTMLElement;
      if (target.style) {
        const originalBg = target.style.backgroundColor || '';
        target.style.backgroundColor = '#dbeafe';
        setTimeout(() => {
          if (target && target.style) {
            target.style.backgroundColor = originalBg;
          }
        }, 300);
      }
    }
  };

  // Listener global para paste quando o modal está aberto
  useEffect(() => {
    if (!aberto) return;

    const handleGlobalPaste = (e: ClipboardEvent) => {
      // Verificar se há uma imagem no clipboard
      const items = e.clipboardData?.items;
      if (!items) return;

      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          hasImage = true;
          break;
        }
      }

      if (!hasImage) return;

      // Processar todas as imagens e distribuir pelos slots vazios
      handlePasteImagem(e);
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, fotoParte1, fotoParte2, fotoParte3, fotoParte4]);

  return {
    handleUploadImagem,
    handlePasteImagem,
  };
}
